// Server-side helper that turns a `PatternManifest` into the absolute
// URL of the pattern rasteriser route. OG image generation runs server-
// side via Satori, which can only render absolute http(s) URLs — relative
// paths and unresolved hosts both produce empty space.
//
// Resolution order for the origin:
//   1. Explicit `options.origin` — caller's choice (tests, special cases)
//   2. `NEXT_PUBLIC_SITE_URL` — the validated env var (required at build)
//   3. `https://${VERCEL_URL}` — Vercel's preview-deploy fallback
//   4. `http://localhost:3000` — dev server default
//
// The hash returned by `encodeManifestHash` already encodes the theme,
// so light/dark cards CDN-cache under separate keys for free.

import { env } from '@/lib/config/env';
import {
  encodeManifestHash,
  type ManifestThemeName,
  type PatternManifest,
} from '@/lib/pattern-studio';

export interface GetPatternUrlOptions {
  /** Theme to bake into the rasteriser hash. OG cards are rendered on
   *  the brand-dark shell by default; pass `'light'` only when the card
   *  actually sits on a light surface. */
  readonly theme?: ManifestThemeName;
  /** Override the origin used to build the absolute URL. Useful for
   *  preview-environment routing or tests. */
  readonly origin?: string;
}

/** Absolute origin for Satori-fetchable assets under `/public`. */
export function resolveOgOrigin(): string {
  const siteUrl = env.NEXT_PUBLIC_SITE_URL;
  if (typeof siteUrl === 'string' && siteUrl.length > 0) return siteUrl;
  // REASON: VERCEL_URL is a server-only Vercel-injected var, not part of the
  // validated public env schema in @/lib/config/env. Read directly here.
  const vercelUrl = process.env.VERCEL_URL;
  if (typeof vercelUrl === 'string' && vercelUrl.length > 0) return `https://${vercelUrl}`;
  return 'http://localhost:3000';
}

/**
 * Build an absolute URL pointing at the server-side pattern rasteriser
 * (`/api/pattern/[hash].png`) for a given manifest. Use from inside any
 * OG route or other server-rendered surface that needs an entity image
 * — the returned URL is CDN-cacheable and produces a stable PNG every
 * time the same manifest is encoded.
 */
export function getPatternUrl(
  manifest: PatternManifest,
  options: GetPatternUrlOptions = {},
): string {
  const theme: ManifestThemeName = options.theme ?? 'light';
  const origin: string = options.origin ?? resolveOgOrigin();
  const hash = encodeManifestHash(manifest, theme);
  return `${origin}/api/pattern/${hash}.png`;
}
